import React, { useState, useEffect } from 'react';
import { useTranslations } from '../../../localization/useTranslations';
import { getLastVisitedContentUrl } from '../../../utils/getLastVisitedContent';

type Provider = 'google' | 'facebook';

export interface SocialLoginButtonProps {
  provider: 'google' | 'facebook';
  size?: 'small' | 'medium' | 'large'; // default and fallback is large
}

const SocialLoginButton: React.FC<SocialLoginButtonProps> = (props) => {
  const [provider, setProvider] = useState<Provider>(props.provider);
  const translations = useTranslations();

  useEffect(() => {
    setProvider(props.provider);
  }, [props.provider]);

  /**
   * Returns the redirect URL based on the provided social provider.
   * @param provider - The social provider key.
   * @returns The redirect URL.
   */
  function getRedirectUrl(provider: Provider): string {
    const baseUrl = window.crmUrl;
    if (!baseUrl) return '';

    // Construct the return URL with any additional parameters
    const returnUrl = new URL(window.location.href);
    // Add flag for successful social login
    returnUrl.searchParams.append('socialLoginStatus', 'success');
    returnUrl.hash = document.getElementById('remp_lock_anchor') ? 'remp_lock_anchor' : returnUrl.hash;
    const encodedReturnUrl = encodeURIComponent(returnUrl.toString());

    let urlConfig: { [key: string]: string } = {
      sk: `${baseUrl}/social-login/social-sign/sign?social_provider_key=${provider}&success_login_url=${encodedReturnUrl}`,
      hu: `${baseUrl}/social-login/social-sign/sign?social_provider_key=${provider}&success_login_url=${encodedReturnUrl}`,
      default: `${baseUrl}/users/${provider}/sign?url=${encodedReturnUrl}`,
    };

    const referer = getLastVisitedContentUrl();
    if (referer) {
      // Encode the referer URL separately
      const encodedReferer = encodeURIComponent(referer);
      urlConfig.sk += `&referer=${encodedReferer}`;
      urlConfig.default += `&referer=${encodedReferer}`;
    }

    return urlConfig[window.country] || urlConfig['default'];
  }

  const redirectUrl = getRedirectUrl(provider);

  const buttonLabel =
    provider === 'google'
      ? translations?.authLabels?.loginWithGoogle || 'Login with Google'
      : translations?.authLabels?.loginWithFacebook || 'Login with Facebook';

  return (
    <div>
      {redirectUrl && (
        <a
          id={`auth-connect-${provider}`}
          href={redirectUrl}
          rel="noopener noreferrer"
          className={`button button--secondary button--${props.size ?? 'large'} button--${provider} button--full-width`}
          target={provider !== 'facebook' ? '_system' : undefined}
        >
          {buttonLabel}
        </a>
      )}
    </div>
  );
};

export default SocialLoginButton;
